/**
 * Notifier WhatsApp Tools Handlers
 *
 * Pure proxy layer: receives a command name + params, dispatches to the
 * matching thunk tool, and returns the result. No business logic here —
 * the thunks are the single source of truth.
 *
 * Runs only in extension context.
 */

import { isExtensionContext, getClientType, ClientType } from "@common-services/detect-context";
import { WA_TOOL_COMMANDS, EngineToolError, waToolError as makeWaToolError, waToolSuccess } from "../../../bt-common/interfaces/whatsapp-tools-commands.interface";
import type { WaToolResponse } from "../../../bt-common/interfaces/whatsapp-tools-commands.interface";
import type { WaToolArgs, WaToolCommand, WaToolResult, WaGetMessageMediaResult } from "../../../bt-common/interfaces/whatsapp-tool-contracts.interface";
import { unwrapEngineResult } from "@bt-common/interfaces/whatsapp-tools-commands.interface";
import { sendMessageThunkTool } from "@extension-context-content/thunks/WhatsappSendMessageThunk";
import {
    waGetMessageAckTool,
    waGetPollResultsTool,
    waGetMessagesAckDataTool,
    getWaMeInfoTool,
    waGetProfilePicUrlTool,
    waGetChatObjectByChatIdTool,
    waGetChatParticipantsTool,
    waAccountRestrictionTool,
    waChatSendRiskTool,
    waCreateGroupTool,
    waUpdateGroupNameTool,
    waGetGroupDataTool,
    waUpdateGroupDescriptionTool,
    waAddGroupParticipantTool,
    waRemoveGroupParticipantTool,
    waSetGroupAdminRightsTool,
    waRemoveGroupAdminRightsTool,
    waSetGroupPictureTool,
    waLeaveGroupTool,
    waGetNewsletterTool,
    waCreateNewsletterTool,
    waGetCommonGroupsTool,
    waGetMessageByKeyTool,
    waOpenChatTool,
    waValidateStoresTool,
    waReactToMessageThunkTool,
    waForwardMessageTool,
    waStarMessageTool,
    waDeleteMessageTool,
    waPinMessageTool,
    waUnpinMessageTool,
    createStatusTool,
    waGetMessageMediaTool,
    waValidatePhoneNumberOrChatIdTool,
    waTriggerQRTimeoutTool,
    waTriggerLogoutTool,
    isWhatsAppConnectedThunkTool,
    waQueryContactsAndChatsTool,
    waGetLatestChatsAndMessagesTool,
    waQueryVaultMessagesTool,
    waQueryMessagesTool,
    waLoadMessagesFromPhoneTool,
    waGetLinkPreviewTool,
} from "@extension-context-content/thunks/WhatsappThunks";
import {
    waSendSeenTool,
    waClearMessagesTool,
    waDeleteChatTool,
    waArchiveChatTool,
    waUnarchiveChatTool,
    waPinChatTool,
    waUnpinChatTool,
    waMuteChatTool,
    waUnmuteChatTool,
    waMarkChatUnreadTool,
    waSendStateTypingTool,
    waSendStateRecordingTool,
    waClearStateTool,
    waGetChatLabelsTool,
    waGetLabelsTool,
    waChangeLabelsTool,
    waGetPinnedMessagesTool,
    waAddOrEditCustomerNoteTool,
} from "./chat/chat-tools";
import { WhatsappToolsClientActions } from "./wa-tools-client-actions";
import { CHROME_SUPPORTED_COMMANDS, WaGuardedReloadResultSchema, type InsertOnlyIfMissingResult } from "@bt-common/interfaces/whatsapp-tool-contracts.interface";
import { DBReducerMap, userMessagesSelectOneObjectByQuery } from "@common-reducers/DBServiceThunks";
import { getStateDirectly, safeDispatch } from "@common/state/state-provider";
import { retrySendMessageThunk } from "@extension-context-content/thunks/WhatsappSendMessageThunk";
import { waitForRetryThunkStart, RETRY_START_WAIT_MS } from "@extension-context-content/thunks/retry-thunk-entry";
import { createInFlightGate, isMediaSend } from "@extension-context-content/thunks/media-retry-gate";
import { postMessageWithResponse } from "@common/services/post-message-to-whatsapp-functions";

// Media upload helper lives in `@extension/services/media-upload` (extracted so
// the waGetMessageMediaTool thunk can consume it without a circular import).
// Re-exported here for the few legacy callers that still import from this file.
export { uploadMediaFile, type UploadResponse } from "@extension/services/media-upload";

/**
 * Sanitize and slim down a message object for transfer
 * Removes bloated body content from image/video/document messages
 */
export function sanitizeAndSlimMessage(msg: any, ignoreProperties: string[] = []): any {
  const bloatedBodyTypes = ['image', 'video', 'document'];
  const cleanedMsg: any = {};
  Object.entries(msg).forEach(([key, value]) => {
    if (!ignoreProperties.includes(key) && value !== undefined) {
      cleanedMsg[key] = value;
    }
  });
  if (bloatedBodyTypes.includes(msg.type)) {
    // message body contains base64 encoded image or video or document thumbnail, so we
    // remove it to save transfer size
    cleanedMsg['body'] = '';
  }
  // same for image/video quotedMsg, if exists
  if (msg?.quotedMsg && bloatedBodyTypes.includes(msg?.quotedMsg?.type)) {
    // message body contains base64 encoded image or video or document thumbnail, so we
    // remove it to save transfer size
    cleanedMsg['quotedMsg']['body'] = '';
  }
  return cleanedMsg;
}

/** #658 — concurrent media retries per content script; one send is awaited at a time by the pacer, offers up to 3. */
const MEDIA_RETRY_CAP = 3;
/** Longer than the 180s send guard, so a slot is only force-released for a thunk that never settles. */
const MEDIA_RETRY_MAX_HOLD_MS = 240_000;
const mediaRetryGate = createInFlightGate({ cap: MEDIA_RETRY_CAP, maxHoldMs: MEDIA_RETRY_MAX_HOLD_MS });

/**
 * insertOnlyIfMissing (plan A.9.6): put the pending message in the local store
 * if absent, start the retry flow (retrySendMessageThunk decides initial vs
 * retry), and answer exactly `{messageExists, whatsappState}`. A refusal
 * answers the taxonomy error.
 */
async function insertOnlyIfMissing(params: WaToolArgs<typeof WA_TOOL_COMMANDS.insertOnlyIfMissing>): Promise<WaToolResponse<InsertOnlyIfMissingResult>> {
  const message = params?.message as any;
  if (!message) return makeWaToolError('invalid_input', 'Message is required');
  const state = getStateDirectly();
  const messageExists = !!userMessagesSelectOneObjectByQuery(state.DBUserMessageReducer, { _id: message._id });
  if (!messageExists) {
    const dbReducerSlice = DBReducerMap.get('user-messages');
    if (dbReducerSlice) safeDispatch(dbReducerSlice.slice.actions.addOne(message));
  }
  // #547: ack only once the thunk has actually started (bounded — a slow start never blocks the reply).
  const startRetry = (): { started: Promise<boolean>; done: Promise<unknown> } => {
    const started = waitForRetryThunkStart(message._id);
    const done = Promise.resolve(safeDispatch(retrySendMessageThunk({ userMessage: message, retryTrigger: 'mqtt' })));
    return { started, done };
  };
  // #658: media retries run through a small in-flight cap — a campaign tick used to start ~100 uploads at
  // once in this content script. A queued retry is acked at once (its thunk starts when a slot frees; if this
  // content script dies first, the server re-wakes the still-undelivered message). Soft cap: a send whose 180s
  // guard fired keeps running page-side after its slot frees. Text and polls are ungated.
  let startedWait: Promise<boolean> | undefined;
  if (isMediaSend(message)) {
    const outcome = mediaRetryGate.submit(String(message._id), () => {
      const run = startRetry();
      startedWait = run.started;
      // A queued wake is acked before its thunk starts, so nobody awaits `started`: warn here (#547).
      void run.started.then((ok) => { if (!ok) console.warn(`[insertOnlyIfMissing] queued media retry thunk did not start within ${RETRY_START_WAIT_MS} ms, messageId: ${message._id}`); });
      return run.done;
    });
    if (outcome !== 'started') console.log(`[insertOnlyIfMissing] media retry ${outcome} (cap ${MEDIA_RETRY_CAP}), messageId: ${message._id}`);
  } else {
    startedWait = startRetry().started;
  }
  if (startedWait && !(await startedWait)) console.warn(`[insertOnlyIfMissing] retry thunk did not start within ${RETRY_START_WAIT_MS} ms, messageId: ${message._id}`);
  // Loaded on use: the automaton's module graph is page-heavy (and the
  // notifier is imported far and wide).
  // The state is informational: a failed lazy load (dev builds emit it as a separate chunk the page
  // cannot fetch) must degrade to UNKNOWN, never turn an already-started send into an error reply.
  let whatsappState = 'UNKNOWN';
  try {
    const { SessionStatusAutomaton } = await import('@extension-context-content/services/session-status-automaton');
    whatsappState = SessionStatusAutomaton.getInstance().getWaStatus() || 'UNKNOWN';
  } catch (stateError) {
    console.warn('[insertOnlyIfMissing] whatsappState unavailable, replying UNKNOWN', stateError);
  }
  return waToolSuccess({ messageExists, whatsappState });
}

/**
 * THE extension's wire dispatch (plan A.9.5): one handler per command the
 * extension answers on the wire. Its keys ARE the manifest —
 * `CHROME_SUPPORTED_COMMANDS` (bt-common) is tsc-bound to them both ways: a
 * manifest command without a handler, or a handler for an undeclared
 * command, fails to compile. The presence record publishes its keys.
 */
const CHROME_WIRE_HANDLERS: {
  [C in keyof typeof CHROME_SUPPORTED_COMMANDS & WaToolCommand]: (params: WaToolArgs<C>) => Promise<WaToolResponse<unknown>>;
} = {
  [WA_TOOL_COMMANDS.waQueryContactsAndChatsTool]: waQueryContactsAndChatsTool,
  [WA_TOOL_COMMANDS.waGetLatestChatsAndMessagesTool]: waGetLatestChatsAndMessagesTool,
  [WA_TOOL_COMMANDS.waQueryVaultMessagesTool]: waQueryVaultMessagesTool,
  [WA_TOOL_COMMANDS.waQueryMessagesTool]: waQueryMessagesTool,
  [WA_TOOL_COMMANDS.waLoadMessagesFromPhoneTool]: waLoadMessagesFromPhoneTool,
  [WA_TOOL_COMMANDS.waGetChatParticipantsTool]: waGetChatParticipantsTool,
  [WA_TOOL_COMMANDS.waGetChatObjectByChatIdTool]: waGetChatObjectByChatIdTool,
  [WA_TOOL_COMMANDS.waGetMessageMediaTool]: waGetMessageMediaTool,
  [WA_TOOL_COMMANDS.waSendSeenTool]: waSendSeenTool,
  [WA_TOOL_COMMANDS.waClearMessagesTool]: waClearMessagesTool,
  [WA_TOOL_COMMANDS.waDeleteChatTool]: waDeleteChatTool,
  [WA_TOOL_COMMANDS.waArchiveChatTool]: waArchiveChatTool,
  [WA_TOOL_COMMANDS.waUnarchiveChatTool]: waUnarchiveChatTool,
  [WA_TOOL_COMMANDS.waPinChatTool]: waPinChatTool,
  [WA_TOOL_COMMANDS.waUnpinChatTool]: waUnpinChatTool,
  [WA_TOOL_COMMANDS.waMuteChatTool]: waMuteChatTool,
  [WA_TOOL_COMMANDS.waUnmuteChatTool]: waUnmuteChatTool,
  [WA_TOOL_COMMANDS.waMarkChatUnreadTool]: waMarkChatUnreadTool,
  [WA_TOOL_COMMANDS.waSendStateTypingTool]: waSendStateTypingTool,
  [WA_TOOL_COMMANDS.waSendStateRecordingTool]: waSendStateRecordingTool,
  [WA_TOOL_COMMANDS.waClearStateTool]: waClearStateTool,
  [WA_TOOL_COMMANDS.waGetChatLabelsTool]: waGetChatLabelsTool,
  [WA_TOOL_COMMANDS.waGetLabelsTool]: waGetLabelsTool,
  [WA_TOOL_COMMANDS.waChangeLabelsTool]: waChangeLabelsTool,
  [WA_TOOL_COMMANDS.waGetPinnedMessagesTool]: waGetPinnedMessagesTool,
  [WA_TOOL_COMMANDS.waAddOrEditCustomerNoteTool]: waAddOrEditCustomerNoteTool,
  [WA_TOOL_COMMANDS.waCreateGroupTool]: waCreateGroupTool,
  [WA_TOOL_COMMANDS.waUpdateGroupNameTool]: waUpdateGroupNameTool,
  [WA_TOOL_COMMANDS.waGetGroupDataTool]: waGetGroupDataTool,
  [WA_TOOL_COMMANDS.waUpdateGroupDescriptionTool]: waUpdateGroupDescriptionTool,
  [WA_TOOL_COMMANDS.waAddGroupParticipantTool]: waAddGroupParticipantTool,
  [WA_TOOL_COMMANDS.waRemoveGroupParticipantTool]: waRemoveGroupParticipantTool,
  [WA_TOOL_COMMANDS.waSetGroupAdminRightsTool]: waSetGroupAdminRightsTool,
  [WA_TOOL_COMMANDS.waRemoveGroupAdminRightsTool]: waRemoveGroupAdminRightsTool,
  [WA_TOOL_COMMANDS.waSetGroupPictureTool]: waSetGroupPictureTool,
  [WA_TOOL_COMMANDS.waLeaveGroupTool]: waLeaveGroupTool,
  [WA_TOOL_COMMANDS.waGetCommonGroupsTool]: waGetCommonGroupsTool,
  [WA_TOOL_COMMANDS.getWaMeInfoTool]: getWaMeInfoTool,
  [WA_TOOL_COMMANDS.waGetMessageAckTool]: waGetMessageAckTool,
  [WA_TOOL_COMMANDS.waGetPollResultsTool]: waGetPollResultsTool,
  [WA_TOOL_COMMANDS.waReactToMessageThunkTool]: waReactToMessageThunkTool,
  [WA_TOOL_COMMANDS.waForwardMessageTool]: waForwardMessageTool,
  [WA_TOOL_COMMANDS.waStarMessageTool]: waStarMessageTool,
  [WA_TOOL_COMMANDS.waDeleteMessageTool]: waDeleteMessageTool,
  [WA_TOOL_COMMANDS.waPinMessageTool]: waPinMessageTool,
  [WA_TOOL_COMMANDS.waUnpinMessageTool]: waUnpinMessageTool,
  [WA_TOOL_COMMANDS.createStatusTool]: createStatusTool,
  [WA_TOOL_COMMANDS.waValidatePhoneNumberOrChatIdTool]: waValidatePhoneNumberOrChatIdTool,
  [WA_TOOL_COMMANDS.waGetProfilePicUrlTool]: waGetProfilePicUrlTool,
  [WA_TOOL_COMMANDS.waGetLinkPreviewTool]: waGetLinkPreviewTool,
  [WA_TOOL_COMMANDS.isWhatsAppConnectedThunkTool]: isWhatsAppConnectedThunkTool,
  [WA_TOOL_COMMANDS.waTriggerQRTimeoutTool]: waTriggerQRTimeoutTool,
  [WA_TOOL_COMMANDS.waTriggerLogoutTool]: waTriggerLogoutTool,
  [WA_TOOL_COMMANDS.waGetNewsletterTool]: waGetNewsletterTool,
  [WA_TOOL_COMMANDS.waCreateNewsletterTool]: waCreateNewsletterTool,
  [WA_TOOL_COMMANDS.waAccountRestrictionTool]: waAccountRestrictionTool,
  [WA_TOOL_COMMANDS.waChatSendRiskTool]: waChatSendRiskTool,
  // Pure proxy — the thunk owns key validation + the not_found normalizer.
  [WA_TOOL_COMMANDS.waGetMessageByKeyTool]: waGetMessageByKeyTool,
  [WA_TOOL_COMMANDS.waGetMessagesAckDataTool]: (params) => {
    // Server-mode wire shape is the legacy { waMessagesKeys: string[] }
    // (GetMessagesAckDataParams) — the contract tool validates
    // { messageKeys: {waMessageKey?, messageId?}[] } and rejects the
    // legacy name as "empty". Normalize before dispatch.
    const legacy = (params as any)?.waMessagesKeys;
    const messageKeys = Array.isArray(params?.messageKeys) && params.messageKeys.length > 0
      ? params.messageKeys
      : (Array.isArray(legacy)
          ? legacy
              .filter((k: unknown): k is string => typeof k === 'string' && k.length > 0)
              .map((k: string) => ({ waMessageKey: k }))
          : []);
    return waGetMessagesAckDataTool({ messageKeys });
  },
  // sendMessageThunkTool returns WaToolResponse<T> already; pass through so
  // the notifier doesn't double-wrap a failure as success (BE#96).
  [WA_TOOL_COMMANDS.sendMessageThunkTool]: sendMessageThunkTool,
  // Engine IS a WhatsApp page, so store validation is meaningful here.
  [WA_TOOL_COMMANDS.waValidateStoresTool]: waValidateStoresTool,
  [WA_TOOL_COMMANDS.PING]: async () =>
    waToolSuccess({ message: 'pong', timestamp: new Date().toISOString(), engineType: getClientType() }),
  [WA_TOOL_COMMANDS.currentDateTimeTool]: async () => {
    // Engine-resolved current date/time. The MCP backend uses the returned tz
    // to convert localDueDateParts → UTC dueDate. `source` distinguishes the
    // extension (user's browser tz, trusted) from the gateway (pod env tz,
    // typically UTC).
    const source = getClientType() === ClientType.GATEWAY ? 'gateway' : 'extension';
    const now = new Date();
    const offsetMinutes = -now.getTimezoneOffset();
    // DST: the current offset differs from the standard (smaller of Jan/Jul) one.
    const januaryOffset = -new Date(now.getFullYear(), 0, 1).getTimezoneOffset();
    const julyOffset = -new Date(now.getFullYear(), 6, 1).getTimezoneOffset();
    const stdOffset = Math.min(januaryOffset, julyOffset);
    const dst = offsetMinutes !== stdOffset && januaryOffset !== julyOffset;
    return waToolSuccess({
      tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
      // sv-SE locale gives ISO-like "YYYY-MM-DD HH:mm:ss" output
      iso_local: now.toLocaleString('sv-SE', { timeZoneName: 'short' }),
      iso_utc: now.toISOString(),
      epoch_ms: now.getTime(),
      offset_minutes: offsetMinutes,
      dst,
      source,
    });
  },
  // Contract: `{reason?}` — forwarded to the page reloader (killMyPod(reason)).
  // #1001: the service worker owns the decision (tab focus + chrome.idle); this only relays the backend's request.
  [WA_TOOL_COMMANDS.waGuardedReloadTool]: async (params) => {
    const reply: unknown = await chrome.runtime.sendMessage({ type: 'GUARDED_RELOAD_REQUEST', reason: params.reason });
    const parsed = WaGuardedReloadResultSchema.safeParse(reply);
    return parsed.success ? waToolSuccess(parsed.data) : makeWaToolError('internal', 'guarded reload: no valid reply from the service worker');
  },
  [WA_TOOL_COMMANDS.waReloadPageTool]: async (params) =>
    unwrapEngineResult(await WhatsappToolsClientActions.waReloadPageTool(params?.reason ?? '')),
  [WA_TOOL_COMMANDS.GET_UNLOGGED_WA_MESSAGE_KEY_IF_EXIST_IN_STORE]: async (params) => {
    if (!params?.chatId || !params?.userMessageId) {
      return makeWaToolError('invalid_input', 'chatId and userMessageId are required for getUnloggedWaMessageKey');
    }
    return unwrapEngineResult(await WhatsappToolsClientActions.getUnloggedWaMessageKeyIfExistInStore(
      params.chatId,
      params.userMessageId,
      params.dueDate ? new Date(params.dueDate) : new Date(Date.now() - 20 * 60 * 1000),
    ));
  },
  // Pure proxy — the thunk owns chatId validation + the open-chat call.
  [WA_TOOL_COMMANDS.waOpenChatTool]: waOpenChatTool,
  [WA_TOOL_COMMANDS.CONVERT_WA_LID_SERIALIZED_TO_CUS_SERIALIZED]: async (params) => {
    if (!params?.waLidSerialized) return makeWaToolError('invalid_input', 'waLidSerialized is required');
    // The page result passes through unchanged ({convertedId}; a miss returns
    // the input) — no rebuild, no inner success (plan A.9.2).
    return unwrapEngineResult(await postMessageWithResponse(
      WA_TOOL_COMMANDS.CONVERT_WA_LID_SERIALIZED_TO_CUS_SERIALIZED,
      { waLidSerialized: params.waLidSerialized },
    ));
  },
  [WA_TOOL_COMMANDS.insertOnlyIfMissing]: insertOnlyIfMissing,
};

/**
 * Notifier-side dispatcher for inbound WhatsApp tool commands.
 *
 * Typed-overload + loose-impl pattern (mirrors `postMessageWithResponse`):
 *
 *   - Typed overload — when callers know the command at compile time
 *     (e.g. internal extension code, or the MQTT entry point AFTER it
 *     narrows the inbound method string against `WA_TOOL_ARGS_SCHEMAS`),
 *     `params` is constrained to `WaToolArgs<C>` and the return is
 *     `WaToolResponse<WaToolResult<C>>`. Drift between the contract and
 *     a thunk implementation surfaces as a tsc error inside this file
 *     instead of as a runtime `params.foo === undefined` read.
 *
 *   - Loose overload — for legacy callers / MQTT path that hasn't yet
 *     narrowed `method`, accepts `(string, unknown)`. The impl signature
 *     is loose by necessity (the inbound MQTT envelope carries an
 *     arbitrary string method); the per-thunk signatures below are the
 *     real type contract.
 */
export function processWhatsappInterfaceCommand<C extends WaToolCommand>(
  method: C,
  params: WaToolArgs<C>,
): Promise<WaToolResponse<WaToolResult<C>>>;
export function processWhatsappInterfaceCommand(
  method: string,
  params: unknown,
): Promise<WaToolResponse<unknown>>;
export async function processWhatsappInterfaceCommand(
  method: string,
  params: any,
): Promise<WaToolResponse<unknown>> {
  if (!isExtensionContext()) {
    return makeWaToolError('engine_error', 'processWhatsappInterfaceCommand must only be called in extension context');
  }

  // Normalize legacy 'whatsapp-interface.' prefix to 'whatsappTools.'
  const normalizedMethod = String(method || '').trim()
    .replace(/^whatsapp-interface\./, 'whatsappTools.');

  // Own keys only: a method named like an Object.prototype member
  // (`constructor`, `toString`) is not a command (QA 3d K3).
  const handler = Object.prototype.hasOwnProperty.call(CHROME_WIRE_HANDLERS, normalizedMethod)
    ? (CHROME_WIRE_HANDLERS as Record<string, (params: any) => Promise<WaToolResponse<unknown>>>)[normalizedMethod]
    : undefined;
  return handler ? handler(params) : makeWaToolError('unsupported_command', `Unknown command: ${method}`);
}

// ============================================================================
// Shared Handler Logic (DRY)
// ============================================================================

/** Check if a value is already a WaToolResponse (has `success` and either `data` or `error`) */
function isWaToolResponse(value: any): value is WaToolResponse {
  return value && typeof value === 'object' && typeof value.success === 'boolean' && ('data' in value || 'error' in value);
}

interface HandlerOptions {
  /** Log function to use */
  log: (...args: any[]) => void;
  /** Log prefix for messages */
  logPrefix: string;
}

/**
 * Shared handler for WhatsApp interface requests (DRY)
 * Used by both Socket.IO and MQTT handlers
 */
async function handleWhatsappInterfaceRequestInternal(
  event: any,
  callback: (response: any) => void,
  options: HandlerOptions
): Promise<void> {
  const { log, logPrefix } = options;

  if (event.path !== 'whatsapp-interface') {
    return;
  }

  try {
    log(`${logPrefix} Handling command:`, event.method);
    // MQTT entry point: `method` is an arbitrary string, `params` is
    // truly opaque (the inbound MQTT envelope carries whatever the
    // backend sent). Use the loose overload — the per-thunk signatures
    // inside the switch are the real type contract. Internal extension
    // callers that DO know `C` at compile time get the typed overload
    // and have their args/result shape-checked at the call site.
    const rawMethod: unknown = event.method;
    const methodStr = typeof rawMethod === 'string' ? rawMethod : '';
    const rawParams: unknown = event.entity?.params;
    const response = await processWhatsappInterfaceCommand(methodStr, rawParams);

    // Every tool must return a WaToolResponse. If it doesn't, that's a bug
    // we need to fix in the tool — not silently paper over here.
    if (!isWaToolResponse(response)) {
      console.error(`${logPrefix} Tool returned non-WaToolResponse for ${event.method}. Fix the tool.`, response);
      callback(makeWaToolError('engine_error', `Tool ${event.method} did not return a WaToolResponse`));
      return;
    }

    log(`${logPrefix} Sending response:`, response);
    callback(response);
  } catch (error: any) {
    console.error(`${logPrefix} Error processing request:`, error);
    // A thunk's EngineToolError keeps its taxonomy code; anything else is engine_error.
    callback(makeWaToolError(error instanceof EngineToolError ? error.code : 'engine_error', error?.message || 'Failed to process request'));
  }
}

/**
 * Handle WhatsApp interface events from Socket.IO
 */
export const handleWhatsappInterfaceEvents = async (updateEvent: any, callback: any) => {
  await handleWhatsappInterfaceRequestInternal(updateEvent, callback, {
    log: console.log.bind(console),
    logPrefix: '[Notifier]',
  });
  return null;
};

/**
 * Handle WhatsApp interface requests from MQTT
 * This wraps processWhatsappInterfaceCommand with MQTT-specific error handling
 */
export async function handleWhatsappInterfaceRequest(
  requestEvent: any,
  callback: (response: any) => void,
  log: (...args: any[]) => void = console.log
): Promise<void> {
  await handleWhatsappInterfaceRequestInternal(requestEvent, callback, {
    log,
    logPrefix: '[MQTT]',
  });
}
